import { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ConfirmDialog } from './Modal';
import Icon from './Icon';
import { getAccessToken, isSecretaryContextUser } from '../authStorage';
import { deleteDecisionType, fetchDecisionTypes } from '../decisionTypesApi';
import { useLanguage } from '../i18n/language';
import { useT } from '../i18n/translations';
import './BranchesPage.css';
import './BoardPage.css';

const PAGE_SIZE = 8;

function DecisionTypesPage() {
  const { direction } = useLanguage();
  const t = useT();
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [search, setSearch] = useState('');
  const [currentPage, setPage] = useState(1);
  const [loadingList, setLoadingList] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [accessDenied, setAccessDenied] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  useEffect(() => {
    if (!getAccessToken()) { navigate('/login', { replace: true }); return; }
    if (!isSecretaryContextUser()) { setAccessDenied(true); setLoadingList(false); return; }
    let cancelled = false;
    (async () => {
      setLoadError(''); setLoadingList(true);
      try {
        const rows = await fetchDecisionTypes();
        if (!cancelled) setData(rows);
      } catch (e) {
        if (!cancelled) setLoadError(e instanceof Error ? e.message : t('common.errorLoad'));
      } finally {
        if (!cancelled) setLoadingList(false);
      }
    })();
    return () => { cancelled = true; };
  }, [navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return data.filter((row) => !q || (row.name || '').toLowerCase().includes(q) || (row.description || '').toLowerCase().includes(q));
  }, [data, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const openDelete = (item) => { setDeleteTarget(item); setConfirmOpen(true); };
  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteDecisionType(deleteTarget.id);
      setData((p) => p.map((r) => (r.id === deleteTarget.id ? { ...r, status: 'inactive' } : r)));
      setConfirmOpen(false); setDeleteTarget(null); setPage(1);
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : t('common.errorDelete'));
      setConfirmOpen(false); setDeleteTarget(null);
    }
  };

  if (accessDenied) {
    return (
      <div className="page" dir={direction}>
        <div className="page__topbar">
          <div className="page__topbar-left">
            <span className="page__topbar-title">{t('decisionTypes.title')}</span>
            <span className="page__topbar-sub">{t('decisionTypes.deniedSub')}</span>
          </div>
        </div>
        <p style={{ padding: 24, color: '#78909c', fontFamily: 'Cairo, sans-serif' }}>{t('common.noPerm')}</p>
      </div>
    );
  }

  return (
    <div className="page" dir={direction}>
      <div className="list-page-card list-page-card--unified">
        <header className="list-page-card__header">
          <div className="list-page-card__titles">
            <span className="list-page-card__title">
              <Icon name="decisions" size={18} color="#00334a" style={{ verticalAlign: 'middle', marginLeft: 6 }} />
              {t('decisionTypes.title')}
            </span>
            <span className="list-page-card__sub">{t('decisionTypes.subtitle')}</span>
          </div>
          <div className="list-page-card__actions">
            <button type="button" className="btn btn--primary" onClick={() => navigate('/app/decision-types/new')}>
              <Icon name="plus" size={14} color="#fff" /> {t('decisionTypes.addType')}
            </button>
          </div>
        </header>
        {loadError && <div className="list-page-card__banner list-page-card__banner--error">{loadError}</div>}
        {loadingList && <div className="list-page-card__loading">{t('decisionTypes.loading')}</div>}
      {!loadingList && (
      <>
      <div className="page__stats">
        <div className="page__stat-chip page__stat-chip--total">
          <Icon name="decisions" size={13} /> {t('decisionTypes.total')}: {data.length} {t('decisionTypes.typeUnit')}
        </div>
      </div>

      <div className="page__filters">
        <div className="page__search">
          <Icon name="search" size={14} color="#90a4ae" className="page__search-icon" />
          <input className="page__search-input" placeholder={t('decisionTypes.search')} value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} />
          {search && (
            <span style={{ cursor: 'pointer', color: '#90a4ae', fontSize: '0.8rem' }} onClick={() => setSearch('')} role="button" tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && setSearch('')}>✕</span>
          )}
        </div>
      </div>

      <div className="table-card">
        <table className="data-table">
          <thead>
            <tr>
              <th style={{ width: 42 }}>#</th>
              <th>{t('decisionTypes.nameCol')}</th>
              <th>{t('decisionTypes.descCol')}</th>
              <th>{t('decisionTypes.dateCol')}</th>
              <th>{t('decisionTypes.actionsCol')}</th>
            </tr>
          </thead>
          <tbody>
            {paginated.length === 0 ? (
              <tr>
                <td colSpan={5}>
                  <div className="empty-state">
                    <div className="empty-state__icon"><Icon name="decisions" size={48} color="#cfd8dc" strokeWidth={1.2} /></div>
                    <div className="empty-state__title">{t('decisionTypes.noMatch')}</div>
                    <div className="empty-state__sub">{t('decisionTypes.noMatchSub')}</div>
                    <button type="button" className="btn btn--primary" onClick={() => navigate('/app/decision-types/new')}>
                      <Icon name="plus" size={14} color="#fff" /> {t('decisionTypes.addType')}
                    </button>
                  </div>
                </td>
              </tr>
            ) : (
              paginated.map((item, idx) => (
                <tr key={item.id}>
                  <td className="td-num">{(currentPage - 1) * PAGE_SIZE + idx + 1}</td>
                  <td><strong style={{ color: '#00334a', fontSize: '0.88rem' }}>{item.name}</strong></td>
                  <td style={{ color: '#546e7a', fontSize: '0.85rem', maxWidth: 360 }}>
                    {item.description ? item.description : <span style={{ color: '#b0bec5' }}>—</span>}
                  </td>
                  <td dir="ltr" style={{ textAlign: 'right', fontSize: '0.85rem' }}>{item.createdAt}</td>
                  <td>
                    <div className="td-actions">
                      <button className="action-btn action-btn--view" title={t('decisionTypes.viewDetails')} type="button" onClick={() => navigate(`/app/decision-types/${item.id}`)}><Icon name="eye" size={14} /></button>
                      <button className="action-btn action-btn--edit" title={t('common.edit')} type="button" onClick={() => navigate(`/app/decision-types/${item.id}/edit`)}><Icon name="edit" size={14} /></button>
                      <button className="action-btn action-btn--delete" title={t('decisionTypes.del')} type="button" onClick={() => openDelete(item)}><Icon name="trash" size={14} /></button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        {filtered.length > PAGE_SIZE && (
          <div className="pagination">
            <span>{t('common.showing')} {Math.min(filtered.length, (currentPage - 1) * PAGE_SIZE + 1)}–{Math.min(filtered.length, currentPage * PAGE_SIZE)} {t('common.of')} {filtered.length}</span>
            <div className="pagination__pages">
              <button className="pagination__btn" type="button" onClick={() => setPage((p) => p - 1)} disabled={currentPage === 1}>‹</button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                <button key={p} type="button" className={`pagination__btn${currentPage === p ? ' pagination__btn--active' : ''}`} onClick={() => setPage(p)}>{p}</button>
              ))}
              <button className="pagination__btn" type="button" onClick={() => setPage((p) => p + 1)} disabled={currentPage === totalPages}>›</button>
            </div>
          </div>
        )}
      </div>
      </>
      )}
      </div>

      <ConfirmDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleDelete}
        itemName={deleteTarget?.name}
        title={t('decisionTypes.confirmDeleteTitle')}
        confirmLabel={t('decisionTypes.del')}
        cancelLabel={t('common.cancel')}
      />
    </div>
  );
}

export default DecisionTypesPage;
